import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";

// --- Animations ---
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(25px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0%, 100% { text-shadow: 0 0 8px rgba(67, 254, 71, 0.4); }
  50%      { text-shadow: 0 0 16px rgba(67, 254, 71, 0.7); }
`;

// --- Styled Components ---
const InsightsHero = styled.section`
  min-height: 100vh;
  padding: 120px 24px 80px;
  background: transparent;
  color: #0f0;
  font-family: 'VT323', monospace;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 20%, rgba(67, 254, 71, 0.08) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 960px;
  margin: 0 auto 80px;
`;

const MainTitle = styled.h1`
  font-size: 3.8rem;
  color: #43fe47;
  text-transform: uppercase;
  letter-spacing: 5px;
  margin: 0 0 16px;
  animation: ${glowPulse} 4s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.45rem;
  color: #aaa;
  line-height: 1.5;
  max-width: 760px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 40px;
  max-width: 1440px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: rgba(8, 20, 8, 0.94);
  border: 1px solid #1f3a1f;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  opacity: 0;
  ${props => props.visible && css`
    animation: ${fadeInUp} 0.8s ease-out forwards;
    animation-delay: ${props.delay}s;
  `}

  &:hover {
    border-color: #43fe47;
    box-shadow: 0 0 35px rgba(67, 254, 71, 0.22);
    transform: translateY(-8px);
  }
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: #0c240c;
  border-bottom: 1px solid #1f3a1f;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ServiceTitle = styled.h2`
  font-size: 1.75rem;
  color: #fff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const TypeBadge = styled.span`
  background: #43fe47;
  color: #000;
  padding: 4px 12px;
  margin: 2px;
  font-size: 0.9rem;
  font-weight: bold;
  border-radius: 3px;
  text-transform: uppercase;
  text-align: center;
`;

const CardBody = styled.div`
  padding: 24px 20px;
`;

const SummaryText = styled.p`
  color: #43fe47;
  font-size: 1.15rem;
  line-height: 1.5;
  margin: 0 0 28px;
  opacity: 0.92;
`;

const FooterAction = styled.div`
  padding: 0 20px 24px;
`;

const InquireBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: transparent;
  border: 1px solid #43fe47;
  color: #43fe47;
  font-size: 1.15rem;
  font-family: 'VT323', monospace;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: #43fe47;
    color: #000;
    box-shadow: 0 0 20px rgba(67, 254, 71, 0.6);
  }
`;

// --- Data ---
const services = [
  { name: "Plumbing Services", summary: "24/7 emergency calls + seasonal maintenance contracts mean constant scheduling pressure and missed leads if you're not online-first. Custom booking PWAs, AI quote forms, and automated follow-ups keep jobs flowing and customers loyal." },
  { name: "HVAC Services", summary: "Breakdowns don't wait for business hours — tune-ups and compliance checks pile up fast. AI-driven scheduling agents, predictive maintenance alerts, and client portals turn reactive work into recurring, predictable revenue." },
  { name: "Pest Control", summary: "Infestations drive urgent calls; subscriptions demand consistent reminders and renewals. Smart reminder systems, automated treatment logs, and subscription dashboards reduce no-shows and boost retention effortlessly." },
  { name: "Locksmith Services", summary: "Lockouts happen anytime — trust is built on speed and reliability. Mobile-first emergency intake, real-time ETA tracking, and secure digital key management build repeat business and strong local referrals." },
  { name: "Fire/Water/Mold Restoration", summary: "Insurance claims move fast; documentation and follow-up are critical. AI-assisted intake forms, photo-based damage assessments, and workflow automation speed jobs and improve insurer relationships." },
  { name: "Electrical Services", summary: "Outages and code inspections require precision and quick response. Digital safety checklists, quote generators, and client communication hubs minimize callbacks and position you as the go-to pro." },
  { name: "Commercial Cleaning / Janitorial", summary: "Recurring contracts need flawless scheduling and proof-of-service. Automated bidding tools, nightly checklists via mobile app, and client portals deliver consistency and upsell opportunities." },
  { name: "Septic / Sewer Services", summary: "Backups are emergencies; pumping schedules are recurring gold. Predictive maintenance reminders, route optimization, and digital service logs keep customers compliant and coming back." },
  { name: "Appliance Repair", summary: "People fix instead of replace when it's fast and trusted. AI diagnostic helpers, parts lookup integration, and appointment reminders cut downtime and turn one fix into lifelong clients." },
  { name: "Auto Repair / Mobile Roadside Assistance", summary: "Breakdowns anywhere; fleets need regular maintenance. Mobile dispatch apps, real-time updates, and automated inspection reports streamline roadside calls and build fleet contracts." },
  { name: "Home Security / Alarm Monitoring & Installation", summary: "Alerts never sleep; monitoring is monthly recurring. Smart dashboards, automated test reminders, and integration with local install schedules reduce false alarms and churn." },
  { name: "Security Guard / Fire Watch Services", summary: "Compliance shifts and events demand reliable coverage. Agent scheduling, time-tracking apps, and compliance reporting tools scale operations without chaos." },
  { name: "IT / Managed Cybersecurity Services", summary: "Breaches are urgent; compliance (HIPAA/CMMC) is non-negotiable. Automated monitoring dashboards, incident response playbooks, and client portals make retainers stickier and easier to deliver." },
  { name: "Gutter Cleaning / Roof Maintenance", summary: "Rainy Seattle means seasonal rushes and leak emergencies. Predictive scheduling based on weather data, quote visualizers, and repeat-customer nurturing keep roofs clear and clients happy." },
  { name: "Garage Door Repair", summary: "Opener failures lock people out instantly. Quick-response intake, part lookup AI, and post-job feedback loops turn urgent fixes into trusted local relationships." },
  { name: "Elevator / Escalator Maintenance", summary: "Strict inspections + emergency callouts in commercial buildings. Compliance tracking systems, predictive fault alerts, and digital logbooks ensure uptime and long-term contracts." },
];

const LocalServiceInsights = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
  }, []);

  const handleInquire = (serviceName) => {
    const subject = encodeURIComponent(`Building AI/Digital Tools for ${serviceName}`);
    window.location.href = `mailto:james@studiovoice2fly.com?subject=${subject}`;
  };

  return (
    <InsightsHero>
      {/* <Header>
        <MainTitle>AI-Powered Systems for Local Service Businesses</MainTitle>
        <Subtitle>
          Running a service business in Seattle? Emergencies, compliance, scheduling chaos, and keeping loyal customers don't have to be manual headaches.<br /><br />
          I build custom digital tools — PWAs for mobile booking & quotes, AI agents for automation & reminders, smart forms that replace endless back-and-forth, dashboards for recurring revenue tracking — tailored exactly to trades like yours.<br /><br />
          Whether you're already operating one of these or eyeing the space as an entrepreneur, let's talk how modern systems can cut admin time, capture more jobs, and make your operation run smoother.
        </Subtitle>
      </Header> */}

      <Grid>
        {services.map((service, index) => (
          <Card
            key={service.name}
            visible={visible}
            delay={index * 0.07}
          >
            <CardHeader>
              <ServiceTitle>{service.name}</ServiceTitle>
              <TypeBadge>LOCAL TRADE</TypeBadge>
            </CardHeader>

            <CardBody>
              <SummaryText>{service.summary}</SummaryText>
            </CardBody>

            <FooterAction>
              <InquireBtn onClick={() => handleInquire(service.name)}>
                [ Inquire — Let's Build for This ]
              </InquireBtn>
            </FooterAction>
          </Card>
        ))}
      </Grid>
    </InsightsHero>
  );
};

export default LocalServiceInsights;